import { useState } from 'react';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { COMPANY } from '../constants';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';
import { trackFormSubmission } from './Tracker';

const API = '/api';

export const QuoteForm = () => {
  const { t, services } = useLanguage();
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      trackFormSubmission();
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18063994223/xadpCJylgpocEO_ayqVD',
          'value': 1.0,
          'currency': 'EUR'
        });
      }
      setFormData({
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        service: '',
        message: ''
      });
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.response?.data?.detail || t.form.errorDefault);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section
        id="devis"
        className="py-16 sm:py-24 lg:py-32 bg-white"
        data-testid="quote-form-section"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 border border-[#41DB23]/30 rounded-xl p-8 sm:p-12 text-center shadow-sm">
            <div className="w-16 h-16 bg-[#41DB23]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#41DB23]" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              {t.form.successTitle}
            </h3>
            <p className="text-gray-600 mb-6">
              {t.form.successMsg}
            </p>
            <Button
              onClick={() => setIsSuccess(false)}
              className="bg-[#41DB23] text-white hover:bg-[#38C41E] font-bold"
              data-testid="new-quote-btn"
            >
              {t.form.successBtn}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="devis"
      className="py-16 sm:py-24 lg:py-32 bg-white"
      data-testid="quote-form-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-bold text-[#41DB23] mb-4 block">
            {t.form.badge}
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-gray-900 mb-4">
            {t.form.title}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            {t.form.subtitle}
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <form
            onSubmit={handleSubmit}
            className="bg-gray-50 border border-gray-200 rounded-xl p-6 sm:p-8 space-y-6 shadow-sm"
            data-testid="quote-form"
          >
            {/* Name Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nom" className="text-gray-700 font-medium">{t.form.nom} *</Label>
                <Input
                  id="nom"
                  name="nom"
                  type="text"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  placeholder={t.form.nomPlaceholder}
                  className="bg-white border-gray-300 focus:border-[#41DB23] focus:ring-1 focus:ring-[#41DB23] text-gray-900 placeholder:text-gray-400"
                  data-testid="input-nom"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prenom" className="text-gray-700 font-medium">{t.form.prenom} *</Label>
                <Input
                  id="prenom"
                  name="prenom"
                  type="text"
                  value={formData.prenom}
                  onChange={handleChange}
                  required
                  placeholder={t.form.prenomPlaceholder}
                  className="bg-white border-gray-300 focus:border-[#41DB23] focus:ring-1 focus:ring-[#41DB23] text-gray-900 placeholder:text-gray-400"
                  data-testid="input-prenom"
                />
              </div>
            </div>

            {/* Contact Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telephone" className="text-gray-700 font-medium">{t.form.telephone} *</Label>
                <Input
                  id="telephone"
                  name="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={handleChange}
                  required
                  placeholder={t.form.telephonePlaceholder}
                  className="bg-white border-gray-300 focus:border-[#41DB23] focus:ring-1 focus:ring-[#41DB23] text-gray-900 placeholder:text-gray-400"
                  data-testid="input-telephone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">{t.form.email} *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t.form.emailPlaceholder}
                  className="bg-white border-gray-300 focus:border-[#41DB23] focus:ring-1 focus:ring-[#41DB23] text-gray-900 placeholder:text-gray-400"
                  data-testid="input-email"
                />
              </div>
            </div>

            {/* Service Selection */}
            <div className="space-y-2">
              <Label htmlFor="service" className="text-gray-700 font-medium">{t.form.serviceLabel} *</Label>
              <Select
                value={formData.service}
                onValueChange={handleServiceChange}
                required
              >
                <SelectTrigger
                  className="bg-white border-gray-300 focus:border-[#41DB23] focus:ring-1 focus:ring-[#41DB23] text-gray-900"
                  data-testid="select-service"
                >
                  <SelectValue placeholder={t.form.servicePlaceholder} />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {services.map((service) => (
                    <SelectItem
                      key={service.id}
                      value={service.id}
                      className="text-gray-900 hover:bg-[#41DB23]/10 focus:bg-[#41DB23]/10"
                    >
                      {service.title}
                    </SelectItem>
                  ))}
                  <SelectItem
                    value="autre"
                    className="text-gray-900 hover:bg-[#41DB23]/10 focus:bg-[#41DB23]/10"
                  >
                    {t.form.other}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message" className="text-gray-700 font-medium">{t.form.messageLabel} *</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                placeholder={t.form.messagePlaceholder}
                className="bg-white border-gray-300 focus:border-[#41DB23] focus:ring-1 focus:ring-[#41DB23] text-gray-900 placeholder:text-gray-400 resize-none"
                data-testid="input-message"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#41DB23] text-white hover:bg-[#38C41E] font-bold uppercase tracking-wide py-4 sm:py-6 text-base sm:text-lg rounded-md flex items-center justify-center gap-3 disabled:opacity-50 shadow-lg"
              data-testid="submit-quote-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t.form.submitting}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {t.form.submitBtn}
                </>
              )}
            </Button>

            {/* Privacy Note */}
            <p
              className="text-xs text-gray-500 text-center"
              dangerouslySetInnerHTML={{ __html: t.form.privacyHtml }}
            />
          </form>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
